import React, { useEffect, useState } from "react";
import Select from "react-select";
import ToastMessage, { ToastProvider } from "../../components/ToastMessage";
import probationSettingsService from "../../services/probationSettingsService";
import "bootstrap/dist/css/bootstrap.min.css";
import { Modal, Button, Form } from "react-bootstrap";
import { fireAudit } from "../../utils/auditUtils";

interface ProbationSetting {
  settingID: number;
  organizationID: number;
  employeeType: number;
  probationMonths: number;
  allowExtension: boolean;
  maxExtensionMonths?: number;
  autoConfirmation: boolean;
  confirmationType?: string;
  isActive: boolean;
}

const employeeOptions = [
  { value: 1, label: "Full-Time" },
  { value: 2, label: "Part-Time" },
  { value: 3, label: "Intern" },
  { value: 4, label: "Contract" },
  { value: 5, label: "Consultant" },
];

const confirmationOptions = [
  { value: "1", label: "Manager Approval" },
  { value: "2", label: "HR Approval" },
  { value: "3", label: "Manager + HR Approval" },
  { value: "4", label: "Performance Based Evaluation" },
  { value: "5", label: "Auto Confirmation" },
];

const ProbationSettings: React.FC = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const organizationID: number = user?.organizationID;
  const createdBy: string = user?.userName || "Admin";

  const [data, setData] = useState<ProbationSetting[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const [errors, setErrors] = useState<any>({});

  const defaultForm: ProbationSetting = {
    settingID: 0,
    organizationID,
    employeeType: 0,
    probationMonths: 3,
    allowExtension: false,
    maxExtensionMonths: 0,
    autoConfirmation: false,
    confirmationType: "1",
    isActive: true,
  };

  const [form, setForm] = useState<ProbationSetting>(defaultForm);

  const mapData = (res: any): ProbationSetting[] => {
    let parsed = res;

    if (typeof res === "string") {
      parsed = JSON.parse(res);
    }

    if (parsed?.Table) {
      parsed = parsed.Table;
    }

    return parsed.map((x: any) => ({
      settingID: x.SettingID,
      organizationID: x.OrganizationID,
      employeeType: x.EmployeeType,
      probationMonths: x.ProbationMonths,
      allowExtension: x.AllowExtension,
      maxExtensionMonths: x.MaxExtensionMonths,
      autoConfirmation: x.AutoConfirmation,
      confirmationType: x.ConfirmationType,
      isActive: x.IsActive,
    }));
  };

  const loadData = async () => {
    try {
      const res =
        await probationSettingsService.getProbationSettingsAsync(
          organizationID
        );

      const mapped = mapData(res);
      setData(mapped);
    } catch {
      ToastMessage.show("Failed to load data", "error");
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const validateForm = () => {
    const newErrors: any = {};

    if (!form.employeeType) {
      newErrors.employeeType = "Employee Type is required";
    }

    if (!form.probationMonths) {
      newErrors.probationMonths = "Probation Months is required";
    } else if (
      form.probationMonths < 1 ||
      form.probationMonths > 24
    ) {
      newErrors.probationMonths =
        "Probation Months must be between 1 and 24";
    }

    if (form.allowExtension) {
      if (
        !form.maxExtensionMonths ||
        form.maxExtensionMonths <= 0
      ) {
        newErrors.maxExtensionMonths =
          "Max Extension Months is required";
      }

      if (
        form.maxExtensionMonths &&
        form.maxExtensionMonths > form.probationMonths
      ) {
        newErrors.maxExtensionMonths =
          "Extension Months cannot exceed Probation Months";
      }
    }

    if (!form.autoConfirmation && !form.confirmationType) {
      newErrors.confirmationType =
        "Confirmation Type is required";
    }

    const duplicate = data.find(
      (x) =>
        x.employeeType === form.employeeType &&
        x.settingID !== form.settingID
    );

    if (duplicate) {
      newErrors.employeeType =
        "Setting already exists for this Employee Type";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const save = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);

      const payload = {
        ...form,
        organizationID,
        createdBy,
        maxExtensionMonths: form.allowExtension
          ? form.maxExtensionMonths
          : null,
        confirmationType: form.autoConfirmation
          ? null
          : form.confirmationType,
      };

      const res =
        await probationSettingsService.createOrUpdateProbationSettingAsync(
          payload
        );

      const parsed =
        typeof res === "string" ? JSON.parse(res) : res;

      if (parsed[0]?.Value === 1) {
        ToastMessage.show(parsed[0].Message, "success");

        const oldData = form.settingID
          ? data.find(
              (d) => d.settingID === form.settingID
            )
          : null;

        fireAudit(
          form.settingID ? "UPDATE" : "CREATE",
          "ProbationSetting",
          oldData,
          form,
          organizationID,
          createdBy,
          "ProbationSettings"
        );

        setShowModal(false);
        setErrors({});
        loadData();
      } else {
        ToastMessage.show(
          parsed[0]?.Message || "Failed",
          "warning"
        );
      }
    } catch {
      ToastMessage.show("Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const deleteSetting = async (id: number) => {
    if (!id) {
      ToastMessage.show("Invalid ID", "error");
      return;
    }

    if (
      !window.confirm(
        "Are you sure you want to delete this setting?"
      )
    )
      return;

    try {
      const res =
        await probationSettingsService.deleteProbationSettingAsync(
          id,
          createdBy
        );

      const parsed =
        typeof res === "string" ? JSON.parse(res) : res;

      if (parsed[0]?.Value === 1) {
        ToastMessage.show(
          "Deleted successfully",
          "success"
        );

        const oldData = data.find(
          (d) => d.settingID === id
        );

        fireAudit(
          "DELETE",
          "ProbationSetting",
          oldData,
          null,
          organizationID,
          createdBy,
          "ProbationSettings"
        );

        loadData();
      } else {
        ToastMessage.show(
          parsed[0]?.Message,
          "warning"
        );
      }
    } catch {
      ToastMessage.show("Delete failed", "error");
    }
  };

  const edit = (item: ProbationSetting) => {
    setErrors({});
    setForm(item);
    setShowModal(true);
  };

  const openCreate = () => {
    setErrors({});
    setForm(defaultForm);
    setShowModal(true);
  };

  const getEmployeeLabel = (id: number) =>
    employeeOptions.find((x) => x.value === id)?.label;

  return (
    <>
      <ToastProvider />

      <div className="container mt-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4>Probation Settings</h4>

          <button
            className="btn btn-primary"
            onClick={openCreate}
          >
            + Add
          </button>
        </div>

        <table className="table table-hover table-dark-custom">
          <thead>
            <tr>
              <th>Type</th>
              <th>Months</th>
              <th>Extension</th>
              <th>Auto Confirm</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="text-center"
                >
                  No data found
                </td>
              </tr>
            ) : (
              data.map((d) => (
                <tr key={d.settingID}>
                  <td>
                    {getEmployeeLabel(d.employeeType)}
                  </td>

                  <td>{d.probationMonths}</td>

                  <td>
                    {d.allowExtension
                      ? `Yes (${d.maxExtensionMonths})`
                      : "No"}
                  </td>

                  <td>
                    {d.autoConfirmation
                      ? "Auto"
                      : "Manual"}
                  </td>

                  <td>
                    <span
                      className={`badge ${
                        d.isActive
                          ? "bg-success"
                          : "bg-secondary"
                      }`}
                    >
                      {d.isActive
                        ? "Active"
                        : "Inactive"}
                    </span>
                  </td>

                  <td>
                    <button
                      className="btn btn-warning btn-sm me-2"
                      onClick={() => edit(d)}
                    >
                      <i className="bi bi-pencil-square"></i>
                    </button>

                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() =>
                        deleteSetting(d.settingID)
                      }
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {form.settingID === 0
              ? "Add Setting"
              : "Edit Setting"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                Employee Type{" "}
                <span className="text-danger">*</span>
              </Form.Label>

              <Select
                className="org-select"
                classNamePrefix="org-select"
                options={employeeOptions}
                value={employeeOptions.find(
                  (x) =>
                    x.value === form.employeeType
                )}
                onChange={(e) =>
                  setForm({
                    ...form,
                    employeeType:
                      e?.value || 0,
                  })
                }
              />

              {errors.employeeType && (
                <small className="text-danger">
                  {errors.employeeType}
                </small>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Probation Months{" "}
                <span className="text-danger">*</span>
              </Form.Label>

              <Form.Control
                type="number"
                min={1}
                max={24}
                value={form.probationMonths}
                onChange={(e) =>
                  setForm({
                    ...form,
                    probationMonths:
                      parseInt(
                        e.target.value
                      ) || 0,
                  })
                }
              />

              {errors.probationMonths && (
                <small className="text-danger">
                  {errors.probationMonths}
                </small>
              )}
            </Form.Group>

            <Form.Check
              type="checkbox"
              label="Allow Extension"
              checked={form.allowExtension}
              onChange={(e) =>
                setForm({
                  ...form,
                  allowExtension:
                    e.target.checked,
                })
              }
            />

            {form.allowExtension && (
              <>
                <Form.Control
                  className="mt-2"
                  type="number"
                  min={1}
                  value={
                    form.maxExtensionMonths
                  }
                  placeholder="Max Extension Months"
                  onChange={(e) =>
                    setForm({
                      ...form,
                      maxExtensionMonths:
                        parseInt(
                          e.target.value
                        ) || 0,
                    })
                  }
                />

                {errors.maxExtensionMonths && (
                  <small className="text-danger">
                    {
                      errors.maxExtensionMonths
                    }
                  </small>
                )}
              </>
            )}

            <Form.Check
              className="mt-3"
              type="checkbox"
              label="Auto Confirmation"
              checked={form.autoConfirmation}
              onChange={(e) =>
                setForm({
                  ...form,
                  autoConfirmation:
                    e.target.checked,
                })
              }
            />

            {!form.autoConfirmation && (
              <Form.Group className="mt-3">
                <Form.Label>
                  Confirmation Type{" "}
                  <span className="text-danger">
                    *
                  </span>
                </Form.Label>

                <Select
                  className="org-select"
                  classNamePrefix="org-select"
                  options={
                    confirmationOptions
                  }
                  value={confirmationOptions.find(
                    (x) =>
                      x.value ===
                      form.confirmationType
                  )}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      confirmationType:
                        e?.value || "",
                    })
                  }
                />

                {errors.confirmationType && (
                  <small className="text-danger">
                    {
                      errors.confirmationType
                    }
                  </small>
                )}
              </Form.Group>
            )}
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() =>
              setShowModal(false)
            }
          >
            Cancel
          </Button>

          <Button
            variant="primary"
            onClick={save}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ProbationSettings;